<template>
  <div>
    <!-- Loading state -->
    <b-card
      v-if="isLoading"
      class="p-5"
    >
      <div class="d-flex justify-content-center">
        <b-spinner
          variant="primary"
          label="Loading..."
        />
      </div>
    </b-card>

    <!-- Error state -->
    <b-alert
      v-else-if="error"
      variant="danger"
      show
    >
      {{ error }}
    </b-alert>

    <!-- Content loaded successfully -->
    <div v-else>
      <!-- Header with actions -->
      <b-card
        no-body
        class="mb-3 repository-header"
        aria-label="En-tête du dépôt logistique"
        role="region"
      >
        <div class="d-flex align-items-center justify-content-between p-3">
          <div class="d-flex align-items-center">
            <div
              class="mr-2 rounded-circle bg-light-primary p-2"
              aria-hidden="true"
            >
              <feather-icon
                icon="HomeIcon"
                size="24"
                class="text-primary"
              />
            </div>
            <div>
              <h3
                class="mb-0"
                :aria-label="'Nom du dépôt logistique : ' + repository.name"
              >
                {{ repository.name }}
              </h3>
              <p
                v-if="repository.address"
                class="text-muted mb-0 repository-address"
                :aria-label="'Adresse du dépôt logistique : ' + repository.address"
              >
                {{ repository.address }}
              </p>
              <p
                v-else
                class="text-muted mb-0"
                aria-label="Aucune adresse renseignée pour ce dépôt logistique"
              >
                Pas d'adresse
              </p>
            </div>
          </div>

          <div>
            <b-badge
              pill
              :variant="repository.is_active ? 'light-success' : 'light-danger'"
              class="text-capitalize mr-1"
              :aria-label="repository.is_active ? 'Dépôt logistique actif' : 'Dépôt logistique inactif'"
            >
              {{ repository.active_status || (repository.is_active ? 'Actif' : 'Inactif') }}
            </b-badge>

            <b-badge
              v-if="repository.is_under_rent"
              pill
              variant="light-info"
              class="mr-1"
              aria-label="Dépôt logistique en location"
            >
              En location
            </b-badge>

            <!-- Action dropdown with enhanced options -->
            <b-dropdown
              variant="primary"
              right
              class="ml-1"
              aria-label="Menu d'actions pour le dépôt logistique"
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                />
                <span class="ml-1 d-none d-md-inline">Actions</span>
              </template>

              <b-dropdown-item
                v-if="user.role === 'admin'"
                :to="{ name: 'repository-edit', params: { id: repository.id }}"
                aria-label="Modifier le dépôt logistique"
              >
                <feather-icon
                  icon="EditIcon"
                  size="16"
                  class="mr-1"
                />
                Modifier
              </b-dropdown-item>

              <b-dropdown-item
                v-if="user.role === 'admin'"
                :aria-label="repository.is_active ? 'Désactiver le dépôt logistique' : 'Activer le dépôt logistique'"
                @click="confirmToggleStatus"
              >
                <feather-icon
                  :icon="repository.is_active ? 'XIcon' : 'CheckIcon'"
                  size="16"
                  class="mr-1"
                />
                {{ repository.is_active ? 'Désactiver' : 'Activer' }}
              </b-dropdown-item>

              <b-dropdown-divider />

              <b-dropdown-item
                aria-label="Imprimer les détails du dépôt logistique"
                @click="printRepository"
              >
                <feather-icon
                  icon="PrinterIcon"
                  size="16"
                  class="mr-1"
                />
                Imprimer les détails
              </b-dropdown-item>

              <b-dropdown-item
                aria-label="Exporter les détails du dépôt logistique en PDF"
                @click="exportRepositoryData"
              >
                <feather-icon
                  icon="DownloadIcon"
                  size="16"
                  class="mr-1"
                />
                Exporter en PDF
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
      </b-card>

      <!-- Statistics Cards -->
      <b-row
        class="mb-2"
        aria-label="Statistiques du dépôt logistique"
        role="region"
      >
        <b-col
          lg="3"
          sm="6"
          class="mb-3"
        >
          <b-card class="stat-card text-center">
            <div class="stat-icon bg-light-primary mb-2">
              <feather-icon
                icon="UsersIcon"
                class="text-primary"
                size="24"
              />
            </div>
            <h3 class="stat-title mb-1">
              {{ repository.manager_count || 0 }}
            </h3>
            <p
              class="stat-subtitle text-muted mb-0"
              aria-label="Nombre de managers logistiques"
            >
              Managers logistiques
            </p>
          </b-card>
        </b-col>

        <b-col
          lg="3"
          sm="6"
          class="mb-3"
        >
          <b-card class="stat-card text-center">
            <div class="stat-icon bg-light-success mb-2">
              <feather-icon
                icon="TruckIcon"
                class="text-success"
                size="24"
              />
            </div>
            <h3 class="stat-title mb-1">
              {{ repository.delivery_man_count || 0 }}
            </h3>
            <p
              class="stat-subtitle text-muted mb-0"
              aria-label="Nombre de livreurs logistiques"
            >
              Livreurs logistiques
            </p>
          </b-card>
        </b-col>

        <b-col
          lg="3"
          sm="6"
          class="mb-3"
        >
          <b-card class="stat-card text-center">
            <div class="stat-icon bg-light-warning mb-2">
              <feather-icon
                icon="PackageIcon"
                class="text-warning"
                size="24"
              />
            </div>
            <h3 class="stat-title mb-1">
              {{ repository.active_orders_count || 0 }}
            </h3>
            <p class="stat-subtitle text-muted mb-0">
              Commandes actives
            </p>
          </b-card>
        </b-col>

        <b-col
          lg="3"
          sm="6"
          class="mb-3"
        >
          <b-card class="stat-card text-center">
            <div class="stat-icon bg-light-info mb-2">
              <feather-icon
                icon="CalendarIcon"
                class="text-info"
                size="24"
              />
            </div>
            <h3 class="stat-title mb-1">
              {{ repository.monthly_orders_count || 0 }}
            </h3>
            <p class="stat-subtitle text-muted mb-0">
              Commandes (30j)
            </p>
          </b-card>
        </b-col>
      </b-row>

      <!-- Main Content with Tabs -->
      <b-card no-body>
        <b-tabs
          pills
          card
          nav-wrapper-class="px-2 pt-2 bg-light-primary rounded-top"
          active-nav-item-class="font-weight-bold"
        >
          <!-- Overview Tab -->
          <b-tab active>
            <template #title>
              <feather-icon
                icon="InfoIcon"
                size="16"
                class="mr-1"
              />
              <span>Vue d'ensemble</span>
            </template>

            <!-- KPI cards with mini charts -->
            <b-card
              no-body
              class="mb-3"
            >
              <b-card-header class="pb-0">
                <h5 class="m-0">
                  Indicateurs de Performance Clés (KPIs)
                </h5>
              </b-card-header>

              <b-card-body>
                <b-row>
                  <!-- Staff Efficiency KPI -->
                  <b-col
                    lg="4"
                    md="6"
                    class="mb-2"
                  >
                    <div class="kpi-card-enhanced p-2">
                      <div class="d-flex justify-content-between mb-1">
                        <div class="d-flex align-items-center">
                          <div class="kpi-icon bg-light-primary">
                            <feather-icon
                              icon="Award"
                              class="text-primary"
                              size="16"
                            />
                          </div>
                          <h6 class="mb-0 ml-1">
                            Efficacité du personnel
                          </h6>
                        </div>
                        <b-badge
                          v-b-tooltip.hover
                          pill
                          variant="light-primary"
                          title="Commandes mensuelles par membre du personnel"
                        >
                          Info
                        </b-badge>
                      </div>

                      <div class="kpi-value-container">
                        <h3 class="text-primary mb-0">
                          {{ kpis.staffEfficiency }}
                        </h3>
                        <div class="mini-chart ml-auto">
                          <!-- Mini trend visualization placeholder -->
                          <div class="trend-line-up" />
                        </div>
                      </div>

                      <small class="text-muted">commandes/employé/mois</small>
                    </div>
                  </b-col>

                  <!-- Driver Workload KPI with progress bar -->
                  <b-col
                    lg="4"
                    md="6"
                    class="mb-2"
                  >
                    <div class="kpi-card-enhanced p-2">
                      <div class="d-flex justify-content-between mb-1">
                        <div class="d-flex align-items-center">
                          <div class="kpi-icon bg-light-warning">
                            <feather-icon
                              icon="TruckIcon"
                              class="text-warning"
                              size="16"
                            />
                          </div>
                          <h6 class="mb-0 ml-1">
                            Charge de travail des livreurs
                          </h6>
                        </div>
                        <b-badge
                          v-b-tooltip.hover
                          pill
                          :variant="getWorkloadVariant(kpis.driverWorkload)"
                          class="ml-auto"
                          title="Nombre moyen de commandes actives par livreur. Un nombre élevé indique une forte charge de travail."
                        >
                          {{ getWorkloadLabel(kpis.driverWorkload) }}
                        </b-badge>
                      </div>

                      <div class="kpi-value-container">
                        <h3
                          class="mb-0"
                          :class="getWorkloadTextClass(kpis.driverWorkload)"
                        >
                          {{ kpis.driverWorkload }}
                        </h3>
                        <!-- Add a progress bar for workload visualization -->
                        <div class="workload-progress mt-2">
                          <b-progress
                            :value="getWorkloadPercentage(kpis.driverWorkload)"
                            :variant="getWorkloadVariantSimple(kpis.driverWorkload)"
                            height="6px"
                          />
                          <div class="d-flex justify-content-between mt-1">
                            <small>0</small>
                            <small>Optimale: 30-40</small>
                            <small>75+</small>
                          </div>
                        </div>
                      </div>

                      <small class="text-muted">commandes actives/livreur</small>
                    </div>
                  </b-col>

                  <!-- Daily Order Rate KPI -->
                  <b-col
                    lg="4"
                    md="6"
                    class="mb-2"
                  >
                    <div class="kpi-card-enhanced p-2">
                      <div class="d-flex justify-content-between mb-1">
                        <div class="d-flex align-items-center">
                          <div class="kpi-icon bg-light-success">
                            <feather-icon
                              icon="BarChartIcon"
                              class="text-success"
                              size="16"
                            />
                          </div>
                          <h6 class="mb-0 ml-1">
                            Rythme quotidien
                          </h6>
                        </div>
                        <b-badge
                          v-b-tooltip.hover
                          pill
                          variant="light-success"
                          title="Nombre moyen de commandes traitées par jour au cours des 30 derniers jours"
                        >
                          Info
                        </b-badge>
                      </div>

                      <div class="kpi-value-container">
                        <h3 class="text-success mb-0">
                          {{ kpis.dailyOrderRate }}
                        </h3>
                        <div class="mini-chart ml-auto">
                          <!-- Mini trend visualization placeholder -->
                          <div class="trend-line-up" />
                        </div>
                      </div>

                      <small class="text-muted">commandes/jour</small>
                    </div>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-card>

            <!-- Repository details -->
            <b-row>
              <!-- Details section -->
              <b-col lg="6">
                <b-card
                  title="Détails du dépôt"
                  class="mb-3"
                >
                  <b-list-group flush>
                    <b-list-group-item class="d-flex justify-content-between align-items-center">
                      <span class="text-muted">Téléphone</span>
                      <span class="font-weight-bold">{{ repository.phone || 'Non défini' }}</span>
                    </b-list-group-item>
                    <b-list-group-item class="d-flex justify-content-between align-items-center">
                      <span class="text-muted">Email</span>
                      <span class="font-weight-bold">{{ repository.email || 'Non défini' }}</span>
                    </b-list-group-item>
                    <b-list-group-item class="d-flex flex-column">
                      <span class="text-muted w-100">Adresse</span>
                      <div class="address-display font-weight-bold mt-1">
                        {{ repository.address || 'Non défini' }}
                      </div>
                    </b-list-group-item>
                    <b-list-group-item class="d-flex justify-content-between align-items-center">
                      <span class="text-muted">Espace disponible</span>
                      <span class="font-weight-bold">{{ repository.available_space ? `${repository.available_space} m²` : 'Non défini' }}</span>
                    </b-list-group-item>
                    <b-list-group-item class="d-flex justify-content-between align-items-center">
                      <span class="text-muted">En location</span>
                      <span class="font-weight-bold">{{ repository.is_under_rent ? 'Oui' : 'Non' }}</span>
                    </b-list-group-item>
                    <b-list-group-item
                      v-if="repository.is_under_rent"
                      class="d-flex justify-content-between align-items-center"
                    >
                      <span class="text-muted">Montant du loyer mensuel</span>
                      <span class="font-weight-bold">{{ repository.monthly_rent_amount ? `${repository.monthly_rent_amount} TND` : 'Non défini' }}</span>
                    </b-list-group-item>
                  </b-list-group>
                </b-card>
              </b-col>

              <!-- Timeline section -->
              <b-col lg="6">
                <b-card
                  title="Historique"
                  class="mb-3"
                >
                  <div v-if="repository.created_at || repository.updated_at">
                    <!-- Enhanced timeline -->
                    <div class="enhanced-timeline">
                      <div class="timeline-item">
                        <div class="timeline-item-icon bg-primary">
                          <feather-icon
                            icon="PlusIcon"
                            size="16"
                            class="text-white"
                          />
                        </div>
                        <div class="timeline-point" />
                        <div class="timeline-item-content">
                          <div class="timeline-date">
                            {{ formatDateShort(repository.created_at) }}
                          </div>
                          <div class="timeline-card">
                            <h6 class="text-primary mb-0">
                              Création du dépôt
                            </h6>
                            <p class="mb-0">
                              Le dépôt a été créé dans le système
                            </p>
                          </div>
                        </div>
                      </div>

                      <div
                        v-if="repository.updated_at !== repository.created_at"
                        class="timeline-item"
                      >
                        <div class="timeline-item-icon bg-info">
                          <feather-icon
                            icon="EditIcon"
                            size="16"
                            class="text-white"
                          />
                        </div>
                        <div class="timeline-point" />
                        <div class="timeline-item-content">
                          <div class="timeline-date">
                            {{ formatDateShort(repository.updated_at) }}
                          </div>
                          <div class="timeline-card">
                            <h6 class="text-info mb-0">
                              Dernière modification
                            </h6>
                            <p class="mb-0">
                              Le dépôt a été mis à jour
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <p
                    v-else
                    class="text-muted text-center my-2"
                  >
                    Aucun historique disponible
                  </p>
                </b-card>
              </b-col>
            </b-row>
          </b-tab>

          <!-- Personnel Tab -->
          <b-tab>
            <template #title>
              <feather-icon
                icon="UsersIcon"
                size="16"
                class="mr-1"
              />
              <span>Personnel</span>
            </template>

            <b-row>
              <b-col md="6">
                <b-card
                  title="Managers"
                  class="mb-3"
                >
                  <div
                    v-if="!managers.length"
                    class="text-center py-3"
                  >
                    <feather-icon
                      icon="UsersIcon"
                      size="32"
                      class="text-muted mb-1"
                    />
                    <p class="text-muted">
                      Aucun manager assigné à ce dépôt
                    </p>
                  </div>
                  <b-list-group
                    v-else
                    flush
                  >
                    <b-list-group-item
                      v-for="manager in managers"
                      :key="`manager-${manager.id}`"
                      class="staff-item"
                    >
                      <!-- User avatar and info would go here -->
                      <div class="avatar-placeholder bg-light-primary mr-2">
                        <span>{{ getInitials(manager.name) }}</span>
                      </div>
                      <div>
                        <h6 class="mb-0">
                          {{ manager.name }}
                        </h6>
                        <small>{{ manager.email }}</small>
                      </div>
                    </b-list-group-item>
                  </b-list-group>
                </b-card>
              </b-col>

              <b-col md="6">
                <b-card
                  title="Livreurs"
                  class="mb-3"
                >
                  <!-- Similar content for delivery personnel -->
                  <div
                    v-if="!deliveryStaff.length"
                    class="text-center py-3"
                  >
                    <feather-icon
                      icon="TruckIcon"
                      size="32"
                      class="text-muted mb-1"
                    />
                    <p class="text-muted">
                      Aucun livreur assigné à ce dépôt
                    </p>
                  </div>
                  <b-list-group
                    v-else
                    flush
                  >
                    <!-- Similar staff item structure -->
                  </b-list-group>
                </b-card>
              </b-col>
            </b-row>
          </b-tab>

          <!-- Orders Tab -->
          <b-tab>
            <template #title>
              <feather-icon
                icon="PackageIcon"
                size="16"
                class="mr-1"
              />
              <span>Commandes</span>
              <b-badge
                v-if="repository.active_orders_count"
                variant="primary"
                pill
                class="ml-1"
              >
                {{ repository.active_orders_count }}
              </b-badge>
            </template>

            <b-card
              title="Commandes actives"
              class="mb-3"
            >
              <div class="text-center py-3">
                <p class="text-muted">
                  Fonctionnalité à venir
                </p>
              </div>
            </b-card>
          </b-tab>

          <!-- History Tab with enhanced timeline -->
          <b-tab>
            <template #title>
              <feather-icon
                icon="ClockIcon"
                size="16"
                class="mr-1"
              />
              <span>Historique</span>
            </template>

            <b-card class="mb-3">
              <div v-if="repository.created_at || repository.updated_at">
                <div class="enhanced-timeline">
                  <div class="timeline-item">
                    <div class="timeline-item-icon bg-primary">
                      <feather-icon
                        icon="PlusIcon"
                        size="16"
                        class="text-white"
                      />
                    </div>
                    <div class="timeline-point" />
                    <div class="timeline-item-content">
                      <div class="timeline-date">
                        {{ formatDateShort(repository.created_at) }}
                      </div>
                      <div class="timeline-card">
                        <h6 class="text-primary mb-0">
                          Création du dépôt
                        </h6>
                        <p class="mb-0">
                          Le dépôt a été créé dans le système
                        </p>
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="repository.updated_at !== repository.created_at"
                    class="timeline-item"
                  >
                    <div class="timeline-item-icon bg-info">
                      <feather-icon
                        icon="EditIcon"
                        size="16"
                        class="text-white"
                      />
                    </div>
                    <div class="timeline-point" />
                    <div class="timeline-item-content">
                      <div class="timeline-date">
                        {{ formatDateShort(repository.updated_at) }}
                      </div>
                      <div class="timeline-card">
                        <h6 class="text-info mb-0">
                          Dernière modification
                        </h6>
                        <p class="mb-0">
                          Le dépôt a été mis à jour
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <p
                v-else
                class="text-muted text-center my-2"
              >
                Aucun historique disponible
              </p>
            </b-card>
          </b-tab>
        </b-tabs>
      </b-card>
    </div>

    <!-- Confirmation modal for status toggle -->
    <b-modal
      ref="confirmStatusModal"
      :title="repository.is_active ? 'Désactiver le dépôt' : 'Activer le dépôt'"
      ok-variant="primary"
      cancel-variant="outline-secondary"
      @ok="handleStatusToggle"
    >
      <p>
        Êtes-vous sûr de vouloir {{ repository.is_active ? 'désactiver' : 'activer' }} ce dépôt?
        <template v-if="repository.is_active">
          Cette action pourrait affecter les opérations en cours.
        </template>
      </p>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BSpinner, BAlert, BBadge, BRow, BCol, BListGroup, BCardHeader, BCardBody,
  BListGroupItem, BModal, BTabs, BTab, BProgress, BDropdown, BDropdownItem,
  BDropdownDivider,
} from 'bootstrap-vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import storeAuth from '@/store/store'

export default {
  components: {
    BCard,
    BSpinner,
    BAlert,
    BBadge,
    BRow,
    BCol,
    BListGroup,
    BListGroupItem,
    BModal,
    BTabs,
    BTab,
    BProgress,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BCardHeader,
    BCardBody,
  },
  data() {
    return {
      isLoading: true,
      error: null,
      repository: {},
      user: {},
      kpis: {
        staffEfficiency: 0,
        driverWorkload: 0,
        dailyOrderRate: 0,
      },
      // Dummy data for demo - would be fetched from API
      managers: [],
      deliveryStaff: [],
      loadingStaff: false,
    }
  },

  created() {
    this.user = storeAuth.state.user
    this.fetchRepositoryDetails()
  },

  methods: {

    async fetchRepositoryDetails() {
      try {
        const { id } = this.$route.params
        // Use the comprehensive detailed endpoint
        const { data } = await axios.get(`/api/repositories/detail/${id}/detailed/`)
        this.repository = data
        // Use backend KPIs directly
        this.kpis = {
          staffEfficiency: data.staff_efficiency ? Math.round(data.staff_efficiency) : 0,
          driverWorkload: data.driver_workload ? Math.round(data.driver_workload) : 0,
          dailyOrderRate: data.daily_order_rate ? Math.round(data.daily_order_rate) : 0,
        }
        this.isLoading = false
      } catch (error) {
        console.error('Error fetching repository details:', error)
        this.error = 'Erreur lors du chargement des données du dépôt'
        this.isLoading = false
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return 'Non disponible'
      const date = new Date(dateStr)
      return new Intl.DateTimeFormat('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date)
    },

    confirmToggleStatus() {
      this.$refs.confirmStatusModal.show()
    },

    async handleStatusToggle() {
      try {
        await axios.post(`/api/repositories/toggle-status/${this.repository.id}/`)

        // Update local state
        this.repository.is_active = !this.repository.is_active
        this.repository.active_status = this.repository.is_active ? 'Actif' : 'Inactif'

        // Show success toast
        this.showToast('success', `Le dépôt a été ${this.repository.is_active ? 'activé' : 'désactivé'} avec succès`)
      } catch (error) {
        console.error('Error toggling repository status:', error)
        this.showToast('danger', 'Erreur lors du changement de statut')
      }
    },

    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: variant === 'success' ? 'Succès' : 'Erreur',
          icon: variant === 'success' ? 'CheckCircleIcon' : 'AlertCircleIcon',
          text,
          variant,
        },
      }, {
        position: 'top-right',
      })
    },

    getWorkloadVariant(workload) {
      if (workload <= 30) return 'light-success'
      if (workload <= 60) return 'light-warning'
      return 'light-danger'
    },

    getWorkloadLabel(workload) {
      if (workload <= 30) return 'Faible'
      if (workload <= 60) return 'Normale'
      return 'Élevée'
    },

    getWorkloadTextClass(workload) {
      if (workload <= 30) return 'text-success'
      if (workload <= 60) return 'text-warning'
      return 'text-danger'
    },

    // Update the percentage calculation for the progress bar
    getWorkloadPercentage(ordersPerDeliveryMan) {
      // Consider 75+ orders per delivery person as 100% workload capacity
      return Math.min(Math.round((ordersPerDeliveryMan / 75) * 100), 100)
    },

    async fetchStaffData() {
      // This would fetch actual staff data in a real implementation
      this.loadingStaff = true

      try {
        // Mock data for demonstration
        this.managers = [
          { id: 1, name: 'Jean Dupont', email: 'jean.dupont@example.com' },
        ]
        this.deliveryStaff = [
          { id: 1, name: 'Mohammed Ben Ali', email: 'mbenali@example.com' },
        ]
      } catch (error) {
        console.error('Error fetching staff data:', error)
      } finally {
        this.loadingStaff = false
      }
    },

    getInitials(name) {
      if (!name) return '??'
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
    },

    formatDateShort(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return new Intl.DateTimeFormat('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }).format(date)
    },

    getWorkloadVariantSimple(workload) {
      if (workload <= 30) return 'success'
      if (workload <= 60) return 'warning'
      return 'danger'
    },

    // Mock functions for the new actions
    printRepository() {
      window.print()
    },

    exportRepositoryData() {
      // This would generate a PDF in a real implementation
      this.showToast('info', 'La fonctionnalité d\'exportation sera bientôt disponible')
    },
  },
}
</script>

<style lang="scss" scoped>
.stat-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  }
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.stat-title {
  font-size: 1.8rem;
  font-weight: 600;
}

.stat-subtitle {
  font-size: 0.9rem;
}

.btn-icon {
  width: 2.2rem;
  height: 2.2rem;
  padding: 0;
  display: inline-flex;
  justify-content: center;
  align-items: center;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 3px 5px rgba(0,0,0,0.1);
  }
}

/* Custom timeline styling to replace BTimeline */
.custom-timeline {
  position: relative;
  padding-left: 3rem;
  margin-bottom: 1rem;

  &:before {
    content: '';
    position: absolute;
    left: 1rem;
    top: 0;
    height: 100%;
    width: 2px;
    background-color: #eee;
  }

  .timeline-item {
    position: relative;
    padding-bottom: 1.5rem;

    &:last-child {
      padding-bottom: 0;
    }

    .timeline-item-icon {
      position: absolute;
      left: -3rem;
      width: 2rem;
      height: 2rem;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .timeline-item-content {
      padding-left: 0.5rem;
    }
  }
}

.repository-address {
  white-space: pre-line; /* Preserves line breaks */
  max-width: 500px;
}

.address-display {
  white-space: pre-line; /* Preserves line breaks */
  width: 100%;
  padding: 4px 0;
}

.kpi-card {
  background-color: #f8f8f8;
  border-radius: 0.5rem;
  height: 100%;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 10px rgba(0,0,0,0.1);
  }
}

// Enhanced KPI cards
.kpi-card-enhanced {
  background-color: #fff;
  border-radius: 0.5rem;
  border: 1px solid #ebe9f1;
  height: 100%;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
    border-color: transparent;
  }

  .kpi-icon {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .kpi-value-container {
    display: flex;
    align-items: center;
    margin: 0.5rem 0;
  }

  .mini-chart {
    width: 60px;
    height: 24px;
    position: relative;

    .trend-line-up {
      background: linear-gradient(90deg, rgba(40, 199, 111, 0.2) 0%, rgba(40, 199, 111, 0.3) 50%, rgba(40, 199, 111, 0.4) 100%);
      height: 24px;
      width: 100%;
      clip-path: polygon(0 100%, 20% 60%, 40% 80%, 60% 40%, 80% 20%, 100% 0, 100% 100%);
    }
  }
}

// Enhanced workload indicator
.workload-progress {
  margin-top: 8px;

  .progress {
    border-radius: 10px;
    overflow: hidden;
  }
}

// Enhanced timeline
.enhanced-timeline {
  position: relative;
  padding-left: 2rem;

  &:before {
    content: '';
    position: absolute;
    left: 6px;
    top: 0;
    height: 100%;
    width: 2px;
    background-color: #ebe9f1;
  }

  .timeline-item {
    position: relative;
    padding-bottom: 2rem;

    &:last-child {
      padding-bottom: 0;
    }

    .timeline-item-icon {
      position: absolute;
      left: -2rem;
      top: 0;
      width: 28px;
      height: 28px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 2;
    }

    .timeline-point {
      position: absolute;
      left: -2px;
      top: 13px;
      width: 10px;
      height: 10px;
      background: white;
      border: 2px solid #7367f0;
      border-radius: 50%;
      z-index: 1;
    }

    .timeline-item-content {
      padding-bottom: 10px;

      .timeline-date {
        color: #6e6b7b;
        font-size: 0.85rem;
        margin-bottom: 0.5rem;
      }

      .timeline-card {
        background-color: #f8f8f8;
        border-radius: 6px;
        padding: 1rem;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
      }
    }
  }
}

// Staff list styling
.staff-item {
  display: flex;
  align-items: center;

  .avatar-placeholder {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
  }
}

// Repository header enhancements
.repository-header {
  background-image: linear-gradient(to right, rgba(115, 103, 240, 0.1), rgba(255, 255, 255, 0));
  border-left: 4px solid #7367f0;
}

// Print styles
@media print {
  .btn-icon, .dropdown {
    display: none !important;
  }
}
</style>
